import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { CandidateGridComponent } from '../shared/candidate-grid/candidate-grid.component';
import { CandidateCardModel } from '../shared/candidate-grid/candidate-card.models';
import {
  CipaElectionLiveTrackingDto,
  CipaElectionLiveTrackingResponseDto,
  CipaElectionVotingExperienceDto,
} from './voting.models';
import { VotingHubService } from './voting-hub.service';
import { VotingService } from './voting.service';

/** Intervalo (ms) do watchdog de fallback: dispara a cada minuto e recarrega se não houve sinal por 10 minutos. */
const FALLBACK_INTERVAL_MS = 60_000;
const FALLBACK_IDLE_THRESHOLD_MS = 10 * 60_000;

@Component({
  selector: 'app-voting-live-tracker',
  standalone: true,
  imports: [CommonModule, CandidateGridComponent],
  templateUrl: './voting-live-tracker.component.html',
  styleUrls: ['./voting-live-tracker.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VotingLiveTrackerComponent implements OnInit, OnDestroy {
  @Input({ required: true }) votingData!: CipaElectionVotingExperienceDto;
  @Input({ required: true }) trackingData!: CipaElectionLiveTrackingDto;

  private lastSignalAt = 0;
  private fallbackTimerId: ReturnType<typeof setInterval> | undefined;
  private hubSub: Subscription | undefined;

  constructor(
    private readonly votingHub: VotingHubService,
    private readonly votingService: VotingService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (!this.isElectionActive) {
      return;
    }

    this.connectHub();
    this.startFallbackWatchdog();
  }

  ngOnDestroy(): void {
    this.stopRealtimeTracking();
  }

  private connectHub(): void {
    if (!this.isElectionActive) {
      return;
    }

    const electionId = this.votingData.election.id;

    this.votingHub.connect(electionId).catch(() => {
      // Se falhar a conexão, o fallback de 10 min garante atualização periódica
    });

    this.hubSub = this.votingHub.voteRegistered.subscribe((id) => {
      if (id !== electionId) {
        return;
      }
      this.refreshTracking();
    });
  }

  private startFallbackWatchdog(): void {
    if (!this.isElectionActive) {
      return;
    }

    this.lastSignalAt = Date.now();
    this.fallbackTimerId = setInterval(() => {
      if (Date.now() - this.lastSignalAt >= FALLBACK_IDLE_THRESHOLD_MS) {
        this.refreshTracking();
      }
    }, FALLBACK_INTERVAL_MS);
  }

  private refreshTracking(): void {
    this.lastSignalAt = Date.now();
    this.votingService.getTracking(this.votingData.election.id).subscribe({
      next: (response) => {
        this.trackingData = this.mapTrackingResponse(response);

        if (this.votingData.election.status !== response.status) {
          this.votingData = {
            ...this.votingData,
            election: {
              ...this.votingData.election,
              status: response.status,
            },
          };
        }

        if (response.status !== 'votacao') {
          this.stopRealtimeTracking();
        }

        this.cdr.markForCheck();
      },
    });
  }

  private stopRealtimeTracking(): void {
    this.hubSub?.unsubscribe();
    this.hubSub = undefined;

    if (this.fallbackTimerId !== undefined) {
      clearInterval(this.fallbackTimerId);
      this.fallbackTimerId = undefined;
    }

    const electionId = this.votingData?.election?.id;
    if (electionId) {
      this.votingHub.disconnect(electionId).catch(() => {});
    }
  }

  private mapTrackingResponse(response: CipaElectionLiveTrackingResponseDto): CipaElectionLiveTrackingDto {
    return {
      lastUpdatedAt: response.lastUpdatedAt,
      totalVotes: response.totalVoters,
      blankVotesCount: response.blankVotesCount,
      blankVotesPercentage: response.blankVotesPercentage,
      turnoutPercentage: response.turnoutPercentage,
      voteReceipt: response.voteReceipt,
      partialResults: response.partialResults,
    };
  }

  get trackedCandidates(): CandidateCardModel[] {
    const resultsByCandidateId = new Map(
      this.trackingData.partialResults.map((result) => [result.candidateId, result]),
    );
    const isElectionActive = this.isElectionActive;

    return this.votingData.candidates
      .filter((candidate) => resultsByCandidateId.has(candidate.id))
      .map((candidate) => {
        const result = resultsByCandidateId.get(candidate.id)!;

        return {
          id: candidate.id,
          candidateNumber: candidate.candidateNumber,
          name: candidate.name,
          department: candidate.department,
          position: candidate.position,
          slogan: candidate.slogan,
          photoUrl: candidate.photoUrl,
          yearsAtCompany: candidate.yearsAtCompany,
          totalVotes: result.votesCount,
          votePercentage: result.votePercentage,
          isElectionActive,
          isElected: result.isElected ?? false,
        };
      })
      .sort((left, right) => {
        if ((right.votePercentage ?? 0) !== (left.votePercentage ?? 0)) {
          return (right.votePercentage ?? 0) - (left.votePercentage ?? 0);
        }

        return (right.totalVotes ?? 0) - (left.totalVotes ?? 0);
      });
  }

  get isElectionActive(): boolean {
    return this.votingData.election.status === 'votacao';
  }

  get isElectionClosed(): boolean {
    return this.votingData.election.status === 'encerrada'
      || this.votingData.election.status === 'votacao_encerrada'
      || this.votingData.election.status === 'homologada';
  }

  get hasVoteReceipt(): boolean {
    return !!this.trackingData.voteReceipt;
  }

  get successChip(): string {
    if (this.isElectionClosed && !this.hasVoteReceipt) {
      return 'Eleição encerrada';
    }

    return this.isElectionClosed ? 'Resultado disponível' : 'Votação registrada';
  }

  get successTitle(): string {
    if (this.isElectionClosed && !this.hasVoteReceipt) {
      return 'Esta eleição já foi concluída!';
    }

    if (this.isElectionClosed) {
      return 'Sua participação ficou registrada!';
    }

    return 'Obrigado pela sua participação!';
  }

  get successDescription(): string {
    if (this.isElectionClosed && !this.hasVoteReceipt) {
      return 'O período de votação foi encerrado e não há voto registrado para o seu acesso nesta edição. Mesmo assim, o resultado final permanece disponível temporariamente para consulta.';
    }

    if (this.isElectionClosed) {
      return 'Seu voto foi registrado durante o período da eleição. Como a votação já foi encerrada, abaixo você pode consultar o resultado final desta apuração.';
    }

    return 'Seu voto já foi computado com sucesso. Acompanhe abaixo a apuração parcial da eleição em tempo real.';
  }

  get summaryChip(): string {
    return this.isElectionActive ? 'Acompanhamento em tempo real' : 'Resultado consolidado';
  }

  get summaryTitle(): string {
    return this.isElectionActive ? 'Resumo da apuração parcial' : 'Resumo da apuração final';
  }

  get summaryDescription(): string {
    return this.isElectionActive
      ? 'Os percentuais são atualizados conforme novos votos são registrados nesta eleição.'
      : 'Os percentuais abaixo representam o fechamento da apuração desta eleição e permanecem visíveis por tempo limitado.';
  }

  get resultsChip(): string {
    return this.isElectionActive ? 'Apuração parcial' : 'Resultado final';
  }

  get resultsTitle(): string {
    return this.isElectionActive
      ? 'Candidatos e percentual de votação'
      : 'Candidatos e resultado consolidado';
  }

  get resultsDescription(): string {
    return this.isElectionActive
      ? 'Acompanhe o desempenho parcial dos candidatos desta eleição no momento atual.'
      : 'Confira abaixo a classificação final dos candidatos após o encerramento desta eleição.';
  }

  get trackingPrivacyMessage(): string {
    if (this.isElectionActive) {
      return 'Seu voto permanece protegido e não é identificado na apuração.';
    }

    if (this.hasVoteReceipt) {
      return 'Seu voto permanece protegido e segue sem identificação individual no resultado final.';
    }

    return 'A consulta exibe apenas o resultado consolidado da eleição, sem identificação individual dos participantes.';
  }
}
